import { IExtension } from '@tencent/ark-extensions/lib/types/src/extension-service/extension-service.interface';
import ArkLoader from '@src/js/common/ark-extensions';
import componentConfig from './component-config';
import {
    CloseType,
    DialogType,
    EntranceType,
    ApiErrorCode,
    CommonDialogOptions,
} from './types';
import { POSITIONS } from '@tencent/docs-scenario-components-aid-globle';

/**
 * docs-component-size-limit-dialog
 * 以下方法为对dc组件业务调用封装
 */

/**
 * dc组件加载
 * @param options dialog配置
 */
export async function showDialog(options: CommonDialogOptions & {
    errorCode: number,
    aidOptions: {
        position: string,
        feature: string,
        apprec1: string,
        apprec2: string,
    },
}): Promise<IExtension | undefined> {
    try {
        const dialog = await ArkLoader.extensionService.getExtension(componentConfig.name);
        // 在弹窗之前主动设置正确的 aid，用于商业化上报
        dialog.show(options);
        return dialog;
    } catch (err) {
        window.log.error(`${componentConfig.name} load fail:${err}`);
    }
}

export enum ESpaceAidType {
    RESTORE_SLIDE = 'restore_slide', // 还原文件
    COPY_SLIDE = 'copy_slide', // 生成副本
    INSERT_PIC_SLIDE = 'insert_pic_slide', // 插入图片
    CONVERT_TO_MULTI_DOC = 'converttomultidoc', // 本地文档转多人编辑
    CREATE_DOC_SLIDE = 'createdoc_slide', // 创建文档
}

export interface IAidOptions {
    aidType?: ESpaceAidType,
    aidPosition?: POSITIONS,
}

/**
 * 根据异常码展示弹框，返回值为是否展示了弹框
 * 注：因为弹框加载是异步的，所以此处返回的是promise，弹框load fail按未弹框处理
 * @param errorCode 异常码
 * @param options dialog配置
 * @param aidType
 * @param aidPosition
 */
export async function showDialogWithCode(
    errorCode: ApiErrorCode,
    options: CommonDialogOptions,
    aidType?: ESpaceAidType,
    aidPosition?: POSITIONS,
): Promise<boolean> {
    if (errorCode in ApiErrorCode) {
        // 属于dialog通用弹框范围
        const [apprec1, apprec2, apprec3] = (aidType || '').split('_');
        return !!(await showDialog({
            ...options,
            errorCode,
            aidOptions: {
                position: aidPosition || POSITIONS.DETAIL,
                feature: 'space',
                apprec1,
                apprec2,
                ...(apprec3 && { apprec3 }),
            },
        }));
    }
    // 不在范围内，直接返回false
    return false;
}

/**
 * 生成副本异常场景公用方法（PC、M逻辑统一，因与Dialog强关联，所以放在此处）
 * @param errorCode 异常码
 * @returns 是否已弹dialog（理论上此处弹，则调用处就无需再走后续逻辑了）
 * @param aidOptions
 */
export function backupShowDialog(
    errorCode: ApiErrorCode,
    aidOptions?: IAidOptions,
): Promise<boolean> {
    const { aidPosition, aidType } = aidOptions || {};
    return showDialogWithCode(
        errorCode,
        {
            type: DialogType.SIZE_LIMIT_DUPLICATE_OWNER,
            report: {
                ver9: EntranceType.SELF_BACKUP,
            },
        },
        aidType || ESpaceAidType.COPY_SLIDE,
        aidPosition,
    );
}

/**
 * 还原文件异常场景公用方法（PC、M逻辑统一，因与Dialog强关联，所以放在此处）
 * @param errorCode 异常码
 * @param onClose 取消回调
 * @returns 是否已弹dialog（理论上此处弹，则调用处就无需再走后续逻辑了）
 */
export function recoverShowDialog(
    errorCode: ApiErrorCode,
    onClose?: (closeType: CloseType) => void,
): Promise<boolean> {
    // 弹框类型和埋点上报需要区分还原是否为协作者操作
    const isCooperator = errorCode === ApiErrorCode.COOPERATOR_FAIL;
    return showDialogWithCode(
        errorCode,
        {
            type: isCooperator ? DialogType.SIZE_LIMIT_RECOVER_COLLABOR : DialogType.SIZE_LIMIT_RECOVER_OWNER,
            report: {
                ver9: isCooperator ? EntranceType.COLLABORATOR_RECOVER : EntranceType.SELF_RECOVER,
            },
            // 还原文件取消需要返回回收站（dc组件提供支付弹框的点击取消返回能力）
            needReturnToTrash: true,
            // 还原文件取消需要返回回收站（业务自行处理开通会员弹框的关闭回调）
            onClose,
        },
        ESpaceAidType.RESTORE_SLIDE,
    );
}

export function convertToMultiDocShowDialog(errorCode: ApiErrorCode) {
    return showDialogWithCode(
        errorCode,
        {
            type: DialogType.SIZE_LIMIT_OWNER,
        },
        ESpaceAidType.CONVERT_TO_MULTI_DOC,
    );
}

